<template>
	<article class="d-council-member-list-custom">
		<PageHero
			:title="header.title"
			:description="header.description"
			v-bind="{ breadcrumb }"
		/>

		<!--
			Search
		-->
		<input
			v-for="(item, index) in searchFilters.fields.filter(
				(e) => e.type === 'Hidden'
			)"
			:key="`hidden-${index}`"
			type="hidden"
			:name="item.name"
			:value="item.value"
		/>

		<div class="bg-primary pb-xl px-layout-margin">
			<div
				:class="[
					'flex flex-wrap flex-col gap-x-0 gap-y-sm mb-lg',
					'>=1024:flex-row >=1024:gap-x-layout-gutter gap-y-0',
				]"
			>
				<BaseInputDropdown
					class="w-full min-w-fit >=1024:w-3col"
					:value="computedFilterValues.party"
					:config="computedFilters.party"
					:aria-label="computedFilters.party.placeholder"
					@input="(event) => updateValue('party', event)"
				/>
				<BaseInputDropdown
					class="w-full min-w-fit >=1024:w-3col"
					:value="computedFilterValues.committee"
					:config="computedFilters.committee"
					:aria-label="computedFilters.committee.placeholder"
					@input="(event) => updateValue('committee', event)"
				/>
			</div>

			<LimboSearch
				v-slot="{ data, pagination, action, state }"
				:search-filters="filters"
				:config="{
					enableLiveSearch: true,
					allowSearchFiltersMutation: true,
				}"
				class="relative"
			>
				<div key="news-list-custom__content">
					<Transition
						mode="out-in"
						name="t-council-member-list-custom__content"
					>
						<!-- Loading -->
						<div v-if="state.isLoading" key="loading">
							<SearchLoader class="w-md mx-auto" />
						</div>

						<div v-else-if="data && data.length > 0" key="content">
							<!-- Items -->
							<div
								:class="[
									'grid grid-cols-1 >=1024:grid-cols-4',
									'gap-x-layout-gutter gap-y-md',
								]"
							>
								<div
									v-for="item in data"
									:key="`member-${item.id}`"
									class="relative h-full text-left"
								>
									<button
										:class="[
											'd-council-member-list-custom__card group',
											'w-full h-full flex flex-row >=1024:flex-col',
											'bg-white rounded-md text-left',
										]"
										@click="() => setActiveTarget(item)"
									>
										<div
											:class="[
												'relative',
												'h-full w-128 >=1024:h-auto >=1024:w-full',
												'rounded-l-md >=1024:rounded-b-0 >=1024:rounded-t-md',
												'overflow-hidden flex-shrink-0',
											]"
										>
											<UmbracoImageExt
												v-if="item.image"
												:widths="[
													320,
													480,
													640,
													480 * 2,
													640 * 2,
												]"
												:alt="item.image.altText"
												:source-url="item.image.cropUrl"
												:source-width="item.image.width"
												:source-height="
													item.image.height
												"
												:aspect-ratio="1"
												:class="[
													'h-full bg-primary-light',
													'>=1024:h-auto >=1024:w-full',
													'transform duration-500 ease-smooth-out',
													'<1024:absolute <1024:top-1/2 <1024:left-1/2',
													'<1024:-translate-x-1/2 <1024:-translate-y-1/2',
												]"
											/>
										</div>

										<div v-if="item" class="p-md">
											<div
												:class="[
													'mb-xs uppercase',
													'text-primary-button text-h8',
												]"
												v-text="item.title"
											></div>
											<div
												:class="[
													'mb-4 text-h3',
													'font-semibold',
												]"
												v-text="item.name"
											></div>
											<div
												v-if="item.party"
												class="text-body mb-16"
												v-text="item.party.name"
											></div>
										</div>

										<BaseButton
											tag="div"
											:class="[
												'absolute top-full left-1/2 w-fit',
												'transform -translate-y-1/2 -translate-x-1/2',
											]"
										>
											<template #icon>
												<SvgArrow />
											</template>
										</BaseButton>
									</button>

									<a
										v-if="item.image"
										:href="item.image.url"
										aria-label="Download billede"
										download
										@click="(e) => e.stopPropagation()"
									>
										<BaseButton
											tag="div"
											:class="[
												'hidden >=1024:block',
												'absolute top-8 right-8',
												'bg-white text-black',
											]"
											:in-group="false"
										>
											<template #icon>
												<SvgDownload
													class="
														w-auto
														h-12
														text-white
													"
												/>
											</template>
										</BaseButton>
									</a>
								</div>
							</div>

							<!-- See more -->
							<div
								v-if="
									pagination.total >
									pagination.limit + pagination.offset
								"
								class="w-full flex justify-center"
							>
								<button
									:class="[
										'flex items-center space-x-12 mx-auto',
										'uppercase text-button font-semibold',
										'w-fit text-text hover:text-primary-button',
										'group',
									]"
									@click="action.fetchMore"
								>
									<span
										:class="[
											'flex-shrink-0',
											'duration-200 ease-smooth-out',
										]"
										v-text="'Vis flere'"
									></span>

									<SvgCaret
										:class="[
											'w-12 transform rotate-90',
											'duration-200 ease-smooth-out',
											'group-hover:translate-y-2',
										]"
									/>
								</button>
							</div>
						</div>
						<div v-else key="noresults" class="flex justify-center">
							<p>Ingen resultater fundet</p>
						</div>
					</Transition>
				</div>
			</LimboSearch>
		</div>

		<!--
			Feedback form
		-->
		<FeedbackForm v-if="activateFeedback" class="mb-lg" />

		<!--
			Overlay
		-->
		<Portal to="overlay">
			<Transition
				name="t-council-member-list-custom__overlay"
				:duration="500"
			>
				<div v-if="activeTarget" class="w-full h-full">
					<div
						:class="[
							'council-member-list-custom__overlay-backdrop',
							'absolute top-0 left-0 w-full h-full',
							'bg-primary-button >=1024:opacity-30',
						]"
						@click="() => setActiveTarget(null)"
					></div>

					<div
						:class="[
							'council-member-list-custom__overlay-content',
							'absolute top-0 right-0 h-full',
							'bg-primary-light px-layout-margin py-lg',
							'w-full >=1024:w-auto',
							'overflow-y-auto',
						]"
					>
						<div class="w-full px-24 >=1024:px-0 >=1024:w-3col">
							<!-- Basic Information -->
							<div
								:class="[
									'mb-xs uppercase',
									'text-primary-button text-h8',
								]"
								v-text="activeTarget.title"
							></div>
							<BaseH3
								tag="h2"
								class="mb-4"
								v-text="activeTarget.name"
							></BaseH3>
							<div
								v-if="activeTarget.party"
								class="text-body"
								v-text="activeTarget.party.name"
							></div>

							<!-- Contact Information -->
							<div
								:class="[
									'flex flex-col space-y-xs',
									'w-full items-start',
									{
										'mt-32':
											activeTarget.email ||
											activeTarget.phone ||
											activeTarget.mobile ||
											(activeTarget.address &&
												(activeTarget.address.address ||
													activeTarget.address
														.postalCode ||
													activeTarget.address.city)),
									},
								]"
							>
								<!-- Email -->
								<a
									v-if="activeTarget.email"
									:href="`mailto:${activeTarget.email}`"
									:class="[
										'flex space-x-16 items-start',
										'hover:text-primary-button',
									]"
								>
									<SvgMail class="w-12 mt-6" />

									<div
										:class="[
											'flex-1 whitespace-nowrap underline',
										]"
										v-text="activeTarget.email"
									></div>
								</a>

								<!-- Phone -->
								<a
									v-if="activeTarget.phone"
									:href="`tel:${activeTarget.phone}`"
									:class="[
										'flex space-x-16 items-start',
										'hover:text-primary-button',
									]"
								>
									<SvgPhone class="w-12 mt-6" />

									<div
										:class="[
											'flex-1 whitespace-nowrap underline',
										]"
										v-text="activeTarget.phone"
									></div>
								</a>

								<!-- Mobile -->
								<a
									v-if="activeTarget.mobile"
									:href="`tel:${activeTarget.mobile}`"
									:class="[
										'flex space-x-16 items-start',
										'hover:text-primary-button',
									]"
								>
									<SvgMobile class="w-12 mt-6" />

									<div
										:class="[
											'flex-1 whitespace-nowrap underline',
										]"
										v-text="activeTarget.mobile"
									></div>
								</a>

								<!-- Address -->
								<div
									v-if="
										activeTarget.address &&
										(activeTarget.address.address ||
											activeTarget.address.postalCode ||
											activeTarget.address.city)
									"
									class="flex space-x-16 items-start"
								>
									<SvgLocation class="w-12 mt-6" />

									<div class="flex-1">
										<div
											v-text="
												activeTarget.address.address
											"
										></div>
										<div>
											<span
												v-text="
													activeTarget.address
														.postalCode
												"
											></span>
											<span
												v-text="
													activeTarget.address.city
												"
											></span>
										</div>
									</div>
								</div>
							</div>

							<div
								v-if="
									(activeTarget.committees &&
										activeTarget.committees.length) ||
									(activeTarget.memberOf &&
										activeTarget.memberOf.length)
								"
								class="pt-md mt-md border-t border-primary"
							>
								<!-- Comittees -->
								<div
									v-if="
										activeTarget.committees &&
										activeTarget.committees.length
									"
									class="mb-32"
								>
									<BaseH6
										tag="h3"
										class="mb-xs"
										v-text="'Politiske udvalg'"
									></BaseH6>

									<ul>
										<li
											v-for="(
												group, index
											) in activeTarget.committees"
											:key="`comittee-${index}`"
											:class="[
												'd-council-member-list-custom__group',
												'relative pl-sm',
											]"
										>
											<NuxtLinkExt
												v-if="group"
												:to="group.url"
												:target="group.target"
												:class="[
													'underline hover:text-primary-button',
												]"
												v-text="
													`${
														group.isChairman
															? 'Formand for '
															: ''
													}${
														group.isViceChairman
															? 'Næstformand for '
															: ''
													}${group.name}`
												"
											></NuxtLinkExt>
										</li>
									</ul>
								</div>

								<!-- Member of -->
								<div
									v-if="
										activeTarget.memberOf &&
										activeTarget.memberOf.length
									"
								>
									<BaseH6
										tag="h3"
										class="mb-xs"
										v-text="'Medlem af'"
									></BaseH6>

									<ul>
										<li
											v-for="(
												group, index
											) in activeTarget.memberOf"
											:key="`member-of-${index}`"
											:class="[
												'd-council-member-list-custom__group',
												'relative pl-sm',
											]"
										>
											<span v-text="group"></span>
										</li>
									</ul>
								</div>
							</div>
						</div>

						<!-- Close button -->
						<BaseButton
							class="fixed top-sm right-sm"
							@click="() => setActiveTarget(null)"
						>
							<template #icon>
								<SvgClose />
							</template>
							<template #default>
								<span v-text="'Luk'"></span>
							</template>
						</BaseButton>
					</div>
				</div>
			</Transition>
		</Portal>
	</article>
</template>

<script>
import SvgArrow from '~/assets/svgs/arrow-icon.svg?inline';
import SvgCaret from '~/assets/svgs/caret-icon.svg?inline';
import SvgClose from '~/assets/svgs/close-icon.svg?inline';
import SvgDownload from '~/assets/svgs/download-icon.svg?inline';
import SvgLocation from '~/assets/svgs/location-icon.svg?inline';
import SvgMail from '~/assets/svgs/mail-icon.svg?inline';
import SvgMobile from '~/assets/svgs/mobile-icon.svg?inline';
import SvgPhone from '~/assets/svgs/phone-icon.svg?inline';
import LimboSearch from '~/citi-baseline/components/LimboSearch';
import PageHero from '~/components/main/PageHero';
import UmbracoImageExt from '~/components/shared/UmbracoImageExt';
import SearchLoader from '~/components/shared/SearchLoader';
import FeedbackForm from '~/components/shared/FeedbackForm';

export default {
	name: 'MainCouncilMemberListCustom',

	components: {
		SvgArrow,
		SvgCaret,
		SvgClose,
		SvgDownload,
		SvgLocation,
		SvgMail,
		SvgMobile,
		SvgPhone,
		LimboSearch,
		PageHero,
		UmbracoImageExt,
		SearchLoader,
		FeedbackForm,
	},

	inheritAttrs: false,

	props: {
		header: {
			type: Object,
			required: true,
		},
		breadcrumb: {
			type: Array,
			required: false,
		},
		searchFilters: {
			type: Object,
			required: true,
		},
		activateFeedback: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			filters: this.searchFilters,
			activeTarget: null,
		};
	},

	computed: {
		computedFilters() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = element;
			});

			return response;
		},
		computedFilterValues() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = Array.isArray(element.value)
					? element.value.find((e) => e.checked)?.value
					: element.value;
			});

			return response;
		},
	},

	methods: {
		updateValue(name, event) {
			const field = this.filters.fields.find((e) => e.name === name);

			if (typeof event === 'string' && Array.isArray(field.value)) {
				field.value.forEach((element) => {
					element.checked = element.value === event;
				});
			} else if (typeof event === 'string') {
				field.value = event;
			} else {
				field.value = event?.target?.value ?? '';
			}
		},

		formatData(data) {
			const response = [...data];
			response.sort((a, b) => {
				if (a.sortOrder !== b.sortOrder) {
					return (a.sortOrder > b.sortOrder) * 2 - 1;
				}

				return 0;
			});

			return response;
		},

		setActiveTarget(target) {
			this.activeTarget = target;
		},
	},
};
</script>

<style lang="postcss">
.d-council-member-list-custom__card:hover .c-umbraco-image {
	@apply scale-110 !important;
}

.t-council-member-list-custom__content-enter-active,
.t-council-member-list-custom__content-leave-active {
	@apply duration-200 ease-smooth-out;
}

.t-council-member-list-custom__content-enter,
.t-council-member-list-custom__content-leave-to {
	@apply opacity-0;
}

.t-council-member-list-custom__overlay-enter-active
	.council-member-list-custom__overlay-backdrop,
.t-council-member-list-custom__overlay-enter-active
	.council-member-list-custom__overlay-content,
.t-council-member-list-custom__overlay-leave-active
	.council-member-list-custom__overlay-backdrop,
.t-council-member-list-custom__overlay-leave-active
	.council-member-list-custom__overlay-content {
	@apply duration-500 ease-smooth-out transform;
}

.t-council-member-list-custom__overlay-enter
	.council-member-list-custom__overlay-backdrop,
.t-council-member-list-custom__overlay-leave-to
	.council-member-list-custom__overlay-backdrop {
	@apply opacity-0 !important;
}

.t-council-member-list-custom__overlay-enter
	.council-member-list-custom__overlay-content,
.t-council-member-list-custom__overlay-leave-to
	.council-member-list-custom__overlay-content {
	@apply translate-x-full;
}

.d-council-member-list-custom__group:before {
	content: '';
	@apply absolute top-1/2 left-0;
	@apply transform -translate-y-1/2;
	@apply w-6 h-6 rounded-full bg-primary-button;
}
</style>
